import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import { Link } from '@appsumo/dorado-react';

import { FTF_MAX_LINKS_TO_DISPLAY } from '~/constants/product';
import type { FTFLink } from '~/types/product';

import LinkIcon from '~/public/font-awesome/link.png';
import ChevronDownIcon from '~/public/font-awesome/chevron-down-bolt.png';

export const VIEW_ALL_LINKS_LABEL = 'View all links';
export const VIEW_LESS_LINKS_LABEL = 'Show less';

export default memo(function FTFHelpfulLinks({
  links,
}: Readonly<{ links: FTFLink[] }>) {
  const [showAllLinks, setShowAllLinks] = useState<boolean>(false);
  const [viewAllLinksLabel, setViewAllLinksLabel] =
    useState<string>(VIEW_ALL_LINKS_LABEL);

  // Default to display all links if there are less than or equal to FTF_MAX_LINKS_TO_DISPLAY.
  useEffect(() => {
    if (links.length <= FTF_MAX_LINKS_TO_DISPLAY) {
      setShowAllLinks(true);
    }
  }, [links.length]);

  const shouldSplitLinks = useMemo(
    () => links.length > FTF_MAX_LINKS_TO_DISPLAY,
    [links.length],
  );

  const linksToDisplay = useMemo(() => {
    if (shouldSplitLinks && !showAllLinks) {
      return links.slice(0, FTF_MAX_LINKS_TO_DISPLAY);
    }

    return links;
  }, [links, shouldSplitLinks, showAllLinks]);

  const toggleViewAllLinks = useCallback(() => {
    setShowAllLinks((prev) => !prev);
    setViewAllLinksLabel((prev) =>
      prev === VIEW_ALL_LINKS_LABEL
        ? VIEW_LESS_LINKS_LABEL
        : VIEW_ALL_LINKS_LABEL,
    );
  }, []);

  if (!links?.length) return null;

  return (
    <ul className="flex flex-col gap-4" data-testid="ftf-helpful-links">
      {linksToDisplay.map((link) => (
        <li key={link.id} data-testid="ftf-helpful-links-link">
          <Link styled href={link.url} target="_blank" title={link.title}>
            <Image
              src={LinkIcon}
              alt="Open link"
              width={20}
              height={16}
              className="mr-2 inline-block h-4 w-5"
            />
            {link.title}
          </Link>
        </li>
      ))}
      {shouldSplitLinks && (
        <li>
          <button
            type="button"
            className="text-base font-normal text-bolt hover:underline"
            onClick={() => toggleViewAllLinks()}
            data-testid="ftf-helpful-links-toggle-button"
          >
            {viewAllLinksLabel}
            <Image
              src={ChevronDownIcon}
              alt="View all links"
              width={16}
              height={16}
              className={`ml-2 inline-block transition-transform duration-300 ${
                showAllLinks ? 'rotate-180' : ''
              }`}
            />
          </button>
        </li>
      )}
    </ul>
  );
});
